import React, { useState } from 'react';
import { Play, LogIn, Loader2, Tv } from 'lucide-react';
import { branding } from '@/config/branding';
import { LOCKED_SERVER_URL, xtreamAPI } from '@/services/xtream-api';
import { useAppStore } from '@/store/app-store';
import toast from 'react-hot-toast';

/**
 * Storefront entry shown in reseller mode before a visitor logs in: lets them
 * browse the catalog as a guest, sign in with an existing line, or subscribe.
 */
export const ResellerLanding: React.FC = () => {
  const {
    enterGuestMode,
    setLoginRequested,
    setAuthInfo,
    brandName,
    subscribeUrl,
    serverUrl,
    showcaseUsername,
    showcasePassword,
  } = useAppStore();
  const [isLoading, setIsLoading] = useState(false);

  const browseAsGuest = async () => {
    if (!showcaseUsername || !showcasePassword) {
      toast.error('Guest browsing is not configured for this storefront.');
      return;
    }
    setIsLoading(true);
    try {
      const creds = {
        serverUrl: serverUrl || LOCKED_SERVER_URL,
        username: showcaseUsername,
        password: showcasePassword,
      };
      const ok = await xtreamAPI.setCredentials(creds);
      if (!ok) {
        toast.error('Showcase catalog is temporarily unavailable.');
        return;
      }
      const authInfo = xtreamAPI.getUserInfo();
      if (authInfo) setAuthInfo(authInfo);
      enterGuestMode(creds);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load catalog');
    } finally {
      setIsLoading(false);
    }
  };

  const subscribe = () => {
    if (subscribeUrl) window.open(subscribeUrl, '_blank', 'noopener');
    else toast('Contact your provider to subscribe.', { icon: '✉️' });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${branding.brandLogo})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-dark-900/90 to-black/85 backdrop-blur-sm" />

      <div className="relative z-10 w-full max-w-2xl text-center">
        <div className="inline-flex items-center gap-3 mb-6">
          <img
            src={branding.brandLogo}
            alt={brandName}
            className="w-16 h-16 rounded-2xl object-cover border-2 border-primary-600 shadow-lg"
          />
          <h1 className="text-4xl font-extrabold text-white">{brandName}</h1>
        </div>

        <p className="text-lg text-gray-300 max-w-xl mx-auto mb-10">
          Thousands of live channels, movies and series. Preview the full catalog, then subscribe to
          start watching in seconds.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={browseAsGuest}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-500 transition-colors focus-strong disabled:opacity-60"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Tv size={20} />}
            Browse catalog
          </button>
          <button
            onClick={subscribe}
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-dark-900 font-semibold hover:bg-gray-200 transition-colors focus-strong"
          >
            <Play size={20} className="fill-dark-900" />
            Subscribe
          </button>
        </div>

        <button
          onClick={() => setLoginRequested(true)}
          className="mt-8 inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors focus-strong"
        >
          <LogIn size={18} />
          I already have an account — Sign in
        </button>
      </div>
    </div>
  );
};
